import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
// Modern stack design tokens + Tailwind utilities (scoped to features/, shared/, entities/).
// Tailwind preflight is OFF and utilities are prefixed `tw-` so this cannot affect Bootstrap.
import "./shared/styles/tokens.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { configureStore } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import rootReducer from "./slices";
import { persistConfig } from "./store/persistConfig";
import { seedFieldMockAuthIfDev } from "./features/field-app/dev/mock-auth";

// DEV-ONLY: seed a mock field-worker session when opening the /field prototype
// directly (no backend yet). Must run BEFORE persistStore() reads sessionStorage
// so auth passes on first render. No-op in production and on non-/field routes.
seedFieldMockAuthIfDev();

// Create persisted reducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

// Configure store with persisted reducer
export const store = configureStore({ 
  reducer: persistedReducer, 
  devTools: true,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore redux-persist actions
        ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
      },
    }),
});

// Create persistor
const persistor = persistStore(store);

// NOTE: Removed manual storage subscription - Redux Persist handles this automatically
// NOTE: Removed global store exposure (window.__REDUX_STORE__) for security
// Redux DevTools works through the Provider, no global exposure needed

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
// Loading component for PersistGate
const PersistGateLoading = () => (
  <div className="d-flex justify-content-center align-items-center vh-100">
    <div className="text-center">
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      <h5 className="mt-3">Loading...</h5>
    </div>
  </div>
);

root.render(
  <Provider store={store}>
    <PersistGate loading={<PersistGateLoading />} persistor={persistor}>
      <React.StrictMode>
        <BrowserRouter 
          basename={import.meta.env.BASE_URL || ""}
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <App />
        </BrowserRouter>
      </React.StrictMode>
    </PersistGate>
  </Provider>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
